/**
 * Sicherung, Aktualisierung, Rückspielung.
 *
 * Das Backend führt hier nichts aus. Es legt einen Auftrag ab, den ein
 * Dienst auf dem Server aufgreift – der kennt nur eine feste Liste von
 * Auftragsarten. Ohne diesen Umweg bräuchte die Weboberfläche Zugriff auf
 * Docker, und das ist auf dieser Maschine gleichbedeutend mit Root.
 *
 * Die Rückspielung ist der eine Vorgang, der alles löschen kann. Sie
 * verlangt deshalb ein getipptes Wort, und zwar nicht als Zierde: der
 * Server weist sie ohne dieses Wort ab.
 */

import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../api/klient'
import { Fehlerhinweis, Ladeanzeige, datumDeutsch } from '../bausteine/Hinweise'

type Abzug = { name: string; bytes: number; erstellt: string }

type Neuerung = { fassung: string; titel: string; punkte: string[] }

type Speicherstand = {
  gesamt_bytes: number
  frei_bytes: number
  belegt_prozent: number
}

type Auslastung = {
  platte: Speicherstand | null
  speicher: Speicherstand | null
  last: { eins: number; fuenf: number; fuenfzehn: number; kerne: number } | null
  laufzeit_sekunden: number | null
  docker: { art: string; rueckgewinnbar: string }[]
}

type Stand = {
  zweig: string
  /** Fassungsnummer aus VERSION – hier und was bereitsteht. */
  fassung: string
  fassung_neu: string
  commit: string
  commit_neu: string
  dahinter: number
  neuerungen: Neuerung[]
  abzuege: Abzug[]
  auslastung: Auslastung
  stand_vom: string
}

type Auftrag = { kennung: string; zustand: string; protokoll: string | null }

function laufzeit(sekunden: number): string {
  const tage = Math.floor(sekunden / 86400)
  const stunden = Math.floor((sekunden % 86400) / 3600)
  if (tage > 0) return `${tage} ${tage === 1 ? 'Tag' : 'Tage'}, ${stunden} h`
  return `${stunden} h`
}

function groesse(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`
  return `${bytes} B`
}

export function Systembereich() {
  const speicher = useQueryClient()
  const [laufender, setzeLaufenden] = useState<string | null>(null)
  const [rueckspielen, setzeRueckspielen] = useState<Abzug | null>(null)

  const stand = useQuery({
    queryKey: ['systemstand'] as const,
    queryFn: ({ signal }) => api.holen<Stand>('/api/system/stand', signal),
    // Solange etwas läuft, öfter nachsehen; sonst reicht selten.
    refetchInterval: laufender ? 5000 : 60000,
  })

  const auftrag = useMutation({
    mutationFn: (daten: { art: string; abzug?: string; bestaetigung?: string }) =>
      api.senden<Auftrag>('/api/system/auftrag', daten),
    onSuccess: (a) => {
      setzeLaufenden(a.kennung)
      setzeRueckspielen(null)
    },
  })

  const auslagern = useMutation({
    mutationFn: (name: string) =>
      api.senden(`/api/system/abzug/${name}/auslagern`, {}),
  })

  /* Der Verlauf des laufenden Auftrags.
     Kein Abonnement, sondern Nachfragen im Takt: ein Auftrag dauert
     Minuten, und eine dauerhafte Verbindung wäre für diese Seltenheit ein
     unangemessener Aufwand. */
  const [verlauf, setzeVerlauf] = useState<Auftrag | null>(null)
  useEffect(() => {
    if (!laufender) return
    let gestoppt = false

    const nachsehen = async () => {
      try {
        const a = await api.holen<Auftrag>(`/api/system/auftrag/${laufender}`)
        if (gestoppt) return
        setzeVerlauf(a)
        if (a.zustand === 'fertig' || a.zustand === 'fehler') {
          setzeLaufenden(null)
          void speicher.invalidateQueries({ queryKey: ['systemstand'] })
        }
      } catch {
        // Beim Aktualisieren startet das Backend neu; ein Aussetzer ist
        // dann normal und kein Grund, die Anzeige abzubrechen.
      }
    }

    void nachsehen()
    const takt = window.setInterval(() => void nachsehen(), 4000)
    return () => {
      gestoppt = true
      window.clearInterval(takt)
    }
  }, [laufender, speicher])

  if (stand.isLoading) return <Ladeanzeige />
  if (stand.error) return <Fehlerhinweis fehler={stand.error} />

  const s = stand.data!
  const aktuell = s.dahinter === 0

  return (
    <div className="space-y-6">
      <Fehlerhinweis fehler={auftrag.error} />
      <Fehlerhinweis fehler={auslagern.error} />

      {verlauf && (
        <div
          className={`karte ${
            verlauf.zustand === 'fehler' ? 'border-stufe-3' : 'border-akzent-500'
          }`}
        >
          <div className="font-semibold">
            {verlauf.zustand === 'laeuft' && 'Läuft …'}
            {verlauf.zustand === 'wartet' && 'Wartet auf den Server …'}
            {verlauf.zustand === 'fertig' && 'Fertig'}
            {verlauf.zustand === 'fehler' && 'Fehlgeschlagen'}
          </div>
          {verlauf.protokoll && (
            <pre className="mt-2 max-h-64 overflow-auto rounded-feld bg-slate-100 p-3 text-sm">
              {verlauf.protokoll}
            </pre>
          )}
        </div>
      )}

      <Auslastungsanzeige werte={s.auslastung} />

      {/* ── Fassung ─────────────────────────────────────────────── */}
      <section className="karte space-y-3">
        <h3 className="text-lg font-bold">Fassung</h3>
        <dl className="grid gap-x-4 gap-y-1 sm:grid-cols-[10rem_1fr]">
          <dt className="text-slate-600">Laufende Fassung</dt>
          <dd>
            <span className="text-lg font-bold">{s.fassung}</span>{' '}
            <span className="font-mono text-base text-slate-600">
              ({s.commit}, {s.zweig})
            </span>
          </dd>
          {!aktuell && (
            <>
              <dt className="text-slate-600">Bereit</dt>
              <dd>
                <span className="text-lg font-bold text-stufe-2">{s.fassung_neu}</span>{' '}
                <span className="font-mono text-base text-slate-600">({s.commit_neu})</span>
              </dd>
            </>
          )}
          <dt className="text-slate-600">Zuletzt nachgesehen</dt>
          <dd>{datumDeutsch(s.stand_vom)}</dd>
        </dl>

        {aktuell ? (
          <p className="text-stufe-ob">Die laufende Fassung ist die neueste.</p>
        ) : (
          <>
            {s.neuerungen.length > 0 ? (
              <div className="space-y-3">
                {s.neuerungen.map((n) => (
                  <div key={n.fassung}>
                    <div className="font-semibold">{n.titel}</div>
                    <ul className="mt-1 list-disc space-y-1 pl-5 text-base">
                      {n.punkte.map((punkt, i) => (
                        <li key={i}>{punkt}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            ) : (
              /* Kein Eintrag in der Änderungsliste, aber neue Commits: dann
                 wurde nachgeschoben, ohne die Fassung zu erhöhen. Das
                 anzuzeigen ist ehrlicher, als so zu tun, als sei nichts. */
              <p className="text-base text-slate-700">
                {s.dahinter} {s.dahinter === 1 ? 'Änderung' : 'Änderungen'} am Programm,
                ohne neue Fassungsnummer.
              </p>
            )}
            <button
              type="button"
              className="knopf-haupt"
              disabled={auftrag.isPending || laufender !== null}
              onClick={() => auftrag.mutate({ art: 'aktualisierung' })}
            >
              Jetzt aktualisieren
            </button>
            <p className="text-base text-slate-600">
              Sichert vorab, baut, schaltet um und prüft, ob die Anwendung
              antwortet. Scheitert der Bau, läuft die alte Fassung
              unverändert weiter. Für ein bis zwei Minuten kann die Anwendung
              nicht erreichbar sein.
            </p>
          </>
        )}
      </section>

      {/* ── Sicherungen ─────────────────────────────────────────── */}
      <section className="karte space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <h3 className="flex-1 text-lg font-bold">Sicherungen</h3>
          <button
            type="button"
            className="knopf-zweit px-3"
            disabled={auftrag.isPending || laufender !== null}
            onClick={() => auftrag.mutate({ art: 'sicherung' })}
          >
            Jetzt sichern
          </button>
        </div>

        <p className="text-base text-slate-700">
          Nächtlich automatisch. Die Abzüge liegen auf derselben Platte wie die
          Anwendung — <strong>laden Sie regelmäßig einen herunter</strong> oder
          lagern Sie ihn in die eingerichtete Dokumentenablage aus. Geht die
          Platte, ist sonst beides weg.
        </p>

        {s.abzuege.length === 0 ? (
          <p className="text-slate-600">Noch kein Abzug vorhanden.</p>
        ) : (
          <ul className="space-y-2">
            {s.abzuege.map((a) => (
              <li key={a.name} className="flex flex-wrap items-center gap-2 border-t-2 border-slate-100 pt-2">
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-base">{a.name}</div>
                  <div className="text-base text-slate-600">
                    {datumDeutsch(a.erstellt)} · {groesse(a.bytes)}
                  </div>
                </div>
                <a
                  className="knopf-still px-3 text-base"
                  href={`/api/system/abzug/${a.name}`}
                >
                  Herunterladen
                </a>
                <button
                  type="button"
                  className="knopf-still px-3 text-base"
                  disabled={auslagern.isPending}
                  onClick={() => auslagern.mutate(a.name)}
                >
                  {auslagern.isPending && auslagern.variables === a.name
                    ? 'Lagert aus …'
                    : 'In die Ablage'}
                </button>
                <button
                  type="button"
                  className="knopf-warnung px-3 text-base"
                  onClick={() => setzeRueckspielen(a)}
                >
                  Zurückspielen
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {rueckspielen && (
        <Rueckspielblatt
          abzug={rueckspielen}
          abbrechen={() => setzeRueckspielen(null)}
          laeuft={auftrag.isPending}
          bestaetigen={(wort) =>
            auftrag.mutate({
              art: 'rueckspielung',
              abzug: rueckspielen.name,
              bestaetigung: wort,
            })
          }
        />
      )}
    </div>
  )
}

/**
 * Die Rückfrage vor der Rückspielung.
 *
 * Ein getipptes Wort und keine Ja-Nein-Frage. Wer „zurueckspielen" schreibt,
 * hat gelesen, was daneben steht — bei einem Knopf mit „Wirklich?" ist das
 * nicht so.
 */
function Rueckspielblatt({
  abzug,
  abbrechen,
  bestaetigen,
  laeuft,
}: {
  abzug: Abzug
  abbrechen: () => void
  bestaetigen: (wort: string) => void
  laeuft: boolean
}) {
  const [wort, setzeWort] = useState('')

  return (
    <div className="karte border-stufe-3 space-y-3">
      <h3 className="text-lg font-bold text-stufe-3">Sicherung zurückspielen</h3>

      <dl className="grid gap-x-4 gap-y-1 sm:grid-cols-[10rem_1fr]">
        <dt className="text-slate-600">Abzug</dt>
        <dd className="font-mono">{abzug.name}</dd>
        <dt className="text-slate-600">Erstellt</dt>
        <dd>{datumDeutsch(abzug.erstellt)}</dd>
      </dl>

      <div className="rounded-feld border-2 border-stufe-3 bg-stufe-3-grund p-3">
        <p className="font-semibold text-stufe-3">
          Die vorhandene Datenbank wird gelöscht.
        </p>
        <p className="mt-1">
          Alles, was seit dem {datumDeutsch(abzug.erstellt)} erfasst wurde — Prüfungen,
          Befunde, Kunden, Belege — ist danach weg. Fotos bleiben unberührt und
          werden nicht mit zurückgespielt.
        </p>
        <p className="mt-1">
          Vor dem Überschreiben wird noch einmal gesichert. Damit lässt sich der
          jetzige Stand notfalls wiederholen — über die Kommandozeile.
        </p>
      </div>

      <div>
        <label htmlFor="r-wort">
          Zum Bestätigen <code>zurueckspielen</code> eingeben
        </label>
        <input
          id="r-wort"
          value={wort}
          onChange={(e) => setzeWort(e.target.value)}
          autoComplete="off"
          spellCheck={false}
        />
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className="knopf-warnung"
          disabled={wort !== 'zurueckspielen' || laeuft}
          onClick={() => bestaetigen(wort)}
        >
          {laeuft ? 'Startet …' : 'Zurückspielen'}
        </button>
        <button type="button" className="knopf-still" onClick={abbrechen}>
          Abbrechen
        </button>
      </div>
    </div>
  )
}


/**
 * Wie es dem Server geht.
 *
 * Die Zahlen kommen vom Betriebssystem und nicht aus der Anwendung: die
 * Anwendung kann nicht wissen, dass die Platte voll ist — sie merkt es
 * erst, wenn nichts mehr geht. Genau davor soll das hier warnen.
 *
 * Gefärbt wird erst ab 80 Prozent und rot ab 90. Eine Anzeige, die immer
 * warnt, warnt nicht.
 */
function Auslastungsanzeige({ werte }: { werte: Auslastung }) {
  const nichts =
    !werte.platte && !werte.speicher && !werte.last && werte.laufzeit_sekunden === null
  if (nichts) return null

  return (
    <section className="karte space-y-3">
      <h3 className="text-lg font-bold">Auslastung</h3>

      <div className="grid gap-3 sm:grid-cols-2">
        {werte.platte && <Balken titel="Platte" stand={werte.platte} />}
        {werte.speicher && <Balken titel="Arbeitsspeicher" stand={werte.speicher} />}
      </div>

      <dl className="flex flex-wrap gap-x-6 gap-y-1 text-base">
        {werte.last && (
          <div>
            <dt className="inline text-slate-600">Last: </dt>
            <dd
              className={`inline font-semibold ${
                werte.last.eins > werte.last.kerne ? 'text-stufe-2' : ''
              }`}
            >
              {werte.last.eins} / {werte.last.fuenf} / {werte.last.fuenfzehn}
              <span className="font-normal text-slate-600">
                {' '}
                bei {werte.last.kerne} Kernen
              </span>
            </dd>
          </div>
        )}
        {werte.laufzeit_sekunden !== null && (
          <div>
            <dt className="inline text-slate-600">Läuft seit: </dt>
            <dd className="inline font-semibold">
              {laufzeit(werte.laufzeit_sekunden)}
            </dd>
          </div>
        )}
      </dl>

      {werte.docker.length > 0 && (
        <div>
          <div className="text-base text-slate-600">
            Von Docker belegt, aber zurückgewinnbar:
          </div>
          <ul className="mt-1 flex flex-wrap gap-x-6 text-base">
            {werte.docker.map((d) => (
              <li key={d.art}>
                <span className="text-slate-600">{d.art}: </span>
                <span className="font-semibold">{d.rueckgewinnbar}</span>
              </li>
            ))}
          </ul>
          <p className="mt-1 text-base text-slate-600">
            Der Bauspeicher wächst mit jeder Aktualisierung. Wird die Platte
            knapp, lässt er sich auf dem Server mit{' '}
            <code>docker builder prune</code> leeren — ohne Folgen für die
            Anwendung.
          </p>
        </div>
      )}
    </section>
  )
}

function Balken({ titel, stand }: { titel: string; stand: Speicherstand }) {
  // Erst ab 80 Prozent färben, ab 90 rot: eine Anzeige, die immer warnt,
  // warnt nicht.
  const farbe =
    stand.belegt_prozent >= 90
      ? 'bg-stufe-3'
      : stand.belegt_prozent >= 80
        ? 'bg-stufe-2'
        : 'bg-marke-600'

  return (
    <div>
      <div className="flex items-baseline justify-between text-base">
        <span className="font-semibold">{titel}</span>
        <span
          className={
            stand.belegt_prozent >= 90
              ? 'font-bold text-stufe-3'
              : stand.belegt_prozent >= 80
                ? 'font-bold text-stufe-2'
                : 'text-slate-700'
          }
        >
          {stand.belegt_prozent} % belegt
        </span>
      </div>
      <div
        className="mt-1 h-3 w-full overflow-hidden rounded-full bg-slate-200"
        role="img"
        aria-label={`${titel}: ${stand.belegt_prozent} Prozent belegt`}
      >
        <div
          className={`h-full ${farbe}`}
          style={{ width: `${Math.min(100, stand.belegt_prozent)}%` }}
        />
      </div>
      <div className="mt-1 text-base text-slate-600">
        {groesse(stand.frei_bytes)} frei von {groesse(stand.gesamt_bytes)}
      </div>
    </div>
  )
}
